import { useState } from "react";
import { useMsal } from "@azure/msal-react";
import { loginRequest, usePopupLogin } from "@/authConfig";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";

export default function Login({ onLogin }) {
  const { instance } = useMsal();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("auth_token", data.token);
      localStorage.setItem("auth_user", JSON.stringify(data.user));
      if (onLogin) onLogin(data.user);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  const handleMicrosoftLogin = async () => {
    setError("");
    setLoading(true);
    if (usePopupLogin) {
      try {
        const response = await instance.loginPopup(loginRequest);
        if (response?.account) {
          instance.setActiveAccount(response.account);
          window.location.reload();
        }
      } catch (e) {
        setError(e.message || "Microsoft login failed");
        setLoading(false);
      }
    } else {
      instance.loginRedirect(loginRequest).catch((e) => {
        setError(e.message || "Microsoft login failed");
        setLoading(false);
      });
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]" data-testid="login-page">
      <div className="w-full max-w-md p-8 bg-white border border-gray-200">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 bg-[#002FA7] flex items-center justify-center text-white font-bold text-sm" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            ER
          </div>
          <div>
            <h1 className="text-xl font-semibold text-[#1A1D20] tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              E-Rate CRM
            </h1>
            <p className="text-xs text-[#6B7280] uppercase tracking-[0.15em]">Consultant Tool</p>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-xs text-red-700" data-testid="login-error">
            {error}
          </div>
        )}

        <form onSubmit={handleEmailLogin} className="space-y-4 mb-6">
          <div>
            <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Email</label>
            <Input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-none" placeholder="admin@nexuserate.com" required />
          </div>
          <div>
            <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Password</label>
            <Input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-none" placeholder="Enter password" required />
          </div>
          <button data-testid="login-submit-btn" type="submit" disabled={loading}
            className="w-full bg-[#002FA7] text-white hover:bg-[#002480] disabled:opacity-50 font-medium px-4 py-3 text-sm transition-colors">
            {loading ? "Signing in..." : "Sign In"}
          </button>
        </form>

        <div className="relative mb-6">
          <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200" /></div>
          <div className="relative flex justify-center"><span className="bg-white px-3 text-xs text-[#6B7280] uppercase tracking-wider">or</span></div>
        </div>

        <button data-testid="microsoft-login-btn" onClick={handleMicrosoftLogin} disabled={loading}
          className="w-full bg-[#1A1D20] text-white hover:bg-[#2d3036] disabled:opacity-50 font-medium px-4 py-3 text-sm flex items-center justify-center gap-3 transition-colors">
          <svg width="20" height="20" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
            <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
            <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
            <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
          </svg>
          Sign in with Microsoft 365
        </button>
      </div>
    </div>
  );
}
